<app-modal
  [isVisible]="isVisible()"
  [config]="modalConfig()"
  [warnBeforeClose]="hasUnsavedWork()"
  (confirmed)="onContinue()"
  (cancelled)="onDismiss()"
  (closed)="onDismiss()"
  (secondary)="onBack()"
>
  @if (isLoading()) {
    <app-modal-status
      variant="loading"
      [title]="'modals.create_control.creating_status' | transloco"
      [description]="'modals.create_control.creating_description' | transloco"
    >
      <app-huxley [state]="'thinking'" [size]="72" />
    </app-modal-status>
  } @else if (successMessage()) {
    <app-modal-status variant="success" [title]="successMessage()!">
      <app-huxley [state]="'success'" [size]="72" />
    </app-modal-status>
  } @else {
    <!-- Step Indicator -->
    <app-step-indicator
      [steps]="stepConfigs()"
      [currentStep]="currentStep()"
      [maxReachedStep]="maxReachedStep()"
      [clickable]="true"
      (stepClicked)="onStepClicked($event)"
    ></app-step-indicator>

    <!-- ── STEP 1: TYPE ── (VER-284 — replaces the previous in-Details
         Single|Composite toggle. Card grid mirrors CreateFrameworkModal's
         'pick' step pattern; three options map 1:1 to the three-state
         row model (compliance/README.md § "Three-state row classification"). -->
    @if (currentStep() === 'type') {
      <div class="step-body">
        <p class="step-hint">{{ 'modals.create_control.type_intro' | transloco }}</p>
        <div class="type-card-grid">
          <button
            type="button"
            class="type-card"
            [class.type-card--selected]="selectedType() === 'custom'"
            (click)="selectType('custom')"
          >
            <mat-icon class="type-card__icon">edit_note</mat-icon>
            <span class="type-card__title">
              {{ 'modals.create_control.card_custom_title' | transloco }}
            </span>
            <span class="type-card__desc">
              {{ 'modals.create_control.card_custom_desc' | transloco }}
            </span>
          </button>
          <button
            type="button"
            class="type-card"
            [class.type-card--selected]="selectedType() === 'template'"
            (click)="selectType('template')"
          >
            <mat-icon class="type-card__icon">bookmark_add</mat-icon>
            <span class="type-card__title">
              {{ 'modals.create_control.card_template_title' | transloco }}
            </span>
            <span class="type-card__desc">
              {{ 'modals.create_control.card_template_desc' | transloco }}
            </span>
          </button>
          <button
            type="button"
            class="type-card"
            [class.type-card--selected]="selectedType() === 'composite'"
            (click)="selectType('composite')"
          >
            <mat-icon class="type-card__icon">library_add_check</mat-icon>
            <span class="type-card__title">
              {{ 'modals.create_control.card_composite_title' | transloco }}
            </span>
            <span class="type-card__desc">
              {{ 'modals.create_control.card_composite_desc' | transloco }}
            </span>
          </button>
        </div>
      </div>
    }

    <!-- ── STEP 2: LANGUAGE ──
         Dedicated language step for EVERY creation type (Custom too).
         Previously the picker was inline on the `pick` step's template-
         section header, which silently skipped the Custom flow — Custom
         controls were always created in English regardless of the user's
         intent. Now the step locks the content language for the whole
         downstream flow (template picks are rendered in this language,
         custom text is authored in it, AI-generated fields honour it). -->
    @if (currentStep() === 'language') {
      <div class="step-body">
        <p class="step-hint">{{ 'language_step.hint_control' | transloco }}</p>
        <app-language-picker
          [language]="templateLanguage()"
          (languageChange)="onLanguageChange($event!)"
          appearance="cards"
        />
      </div>
    }

    <!-- ── STEP 3: PICK ── (Template / Composite only)
         Dedicated picker screen. Rendered exclusively when the user
         chose Template or Composite on the Type step; Custom skips
         directly from Language → Details. Splitting the picker off
         Details gives the templates + composite reuse UX the vertical
         space they deserve without cramping name/category/description
         below. -->
    @if (currentStep() === 'pick') {
      <div class="step-body">
        <div class="step-header-row">
          <p class="step-hint">
            @if (selectedType() === 'template') {
              {{ 'modals.create_control.pick_intro_template' | transloco }}
            } @else {
              {{ 'modals.create_control.pick_intro_composite' | transloco }}
            }
          </p>
        </div>

        @if (selectedType() === 'template' || selectedType() === 'composite') {
          <section class="template-section">
            <header class="template-section__header">
              <div class="template-section__titles">
                <h3 class="template-section__title">
                  @if (selectedType() === 'template') {
                    {{ 'modals.create_control.section_template_title' | transloco }}
                  } @else {
                    {{ 'modals.create_control.section_composite_title' | transloco }}
                  }
                </h3>
                <p class="template-section__hint">
                  @if (selectedType() === 'template') {
                    {{ 'modals.create_control.section_template_hint' | transloco }}
                  } @else {
                    {{ 'modals.create_control.section_composite_hint' | transloco }}
                  }
                </p>
              </div>
              <!-- Language pill (read-only badge). The pick is now a
                   dedicated step earlier in the wizard; showing the
                   value here confirms what the user chose without
                   re-offering it inline. -->
              <app-language-badge [language]="templateLanguage()" appearance="pill" />
            </header>

            @if (templatePickerMode() === 'single') {
              @if (!selectedControlTemplate()) {
                <ng-container [ngTemplateOutlet]="pickerList" />
              } @else {
                <div class="template-selected">
                  <div class="template-selected-info">
                    <!-- `bookmark` is the app-wide glyph for a template-
                         lineage / Linked control (see import-assistant
                         and `getControlTypeIconConfig`).
                         The earlier `link` (chain) implied "connected to
                         two live things" — semantically wrong here since
                         the user is adopting a template, not linking. -->
                    <mat-icon class="template-selected-icon">bookmark</mat-icon>
                    @let selectedTemplate = selectedControlTemplate()!;
                    @let selectedRef = selectedTemplate.control_reference;
                    @if (selectedRef) {
                      @let selectedIdentity = templateFrameworkIdentity(selectedTemplate);
                      <app-control-ref
                        [reference]="selectedRef"
                        [frameworkType]="selectedIdentity?.frameworkType ?? null"
                        [frameworkVersion]="selectedIdentity?.frameworkVersion ?? null"
                        [frameworkName]="selectedIdentity?.frameworkName ?? null"
                      />
                    }
                    <span class="template-selected-name">{{
                      localizedField(selectedTemplate.name, selectedTemplate.name_da)
                    }}</span>
                  </div>
                  <button
                    type="button"
                    class="template-clear"
                    [attr.aria-label]="'modals.create_control.clear_template' | transloco"
                    (click)="clearControlTemplate()"
                  >
                    <mat-icon>close</mat-icon>
                  </button>
                </div>

                @if (reuseCandidatesError()) {
                  <div class="warn-banner reuse-banner" role="alert">
                    <span>{{ 'modals.create_control.template_reuse_error' | transloco }}</span>
                    <button type="button" class="reuse-banner__retry" (click)="retryReuseFetch()">
                      {{ 'modals.create_control.template_reuse_retry' | transloco }}
                    </button>
                  </div>
                }

                <!-- Reuse advisory (VER-284). Non-blocking hint that
                     surfaces when the org already has ≥1 control
                     implementing (or covering, via composite) the
                     picked template. Industry-standard "similar item
                     detected" pattern: informational, offers escape
                     via a "view existing" link, dismissable. Users
                     who genuinely want a duplicate proceed with Next
                     as normal. -->
                @if (
                  reuseCandidates().length > 0 &&
                  !templateReuseHintDismissed() &&
                  !reuseCandidatesError()
                ) {
                  <!-- Reuse advisory (Template mode). Non-blocking
                       "similar item detected" callout. Visual language
                       aligned with <app-composite-reuse-panel>: info-
                       tinted advisory wrapper hosts a neutral card row
                       per existing control (mirrors the composite
                       candidate row shape) so managers read cross-
                       surface reuse feedback consistently. Dismiss
                       clears the hint per-pick, not per-session. -->
                  <aside class="template-reuse-hint" role="note">
                    <header class="template-reuse-hint__header">
                      <mat-icon class="template-reuse-hint__icon" aria-hidden="true">info</mat-icon>
                      <p class="template-reuse-hint__title">
                        {{
                          templateReuseTitleKey() | transloco: { count: reuseCandidates().length }
                        }}
                      </p>
                      <button
                        type="button"
                        class="template-reuse-hint__dismiss"
                        [attr.aria-label]="
                          'modals.create_control.template_reuse_dismiss' | transloco
                        "
                        (click)="dismissTemplateReuseHint()"
                      >
                        <mat-icon>close</mat-icon>
                      </button>
                    </header>
                    <ul class="template-reuse-hint__list" role="list">
                      @for (cand of reuseCandidates(); track cand.control_id) {
                        @let candIdentity = matchFrameworkIdentity(cand);
                        <li class="template-reuse-hint__card">
                          <div class="template-reuse-hint__card-body">
                            <div class="template-reuse-hint__card-title">
                              @if (cand.control_reference) {
                                <app-control-ref
                                  [reference]="cand.control_reference"
                                  [frameworkType]="candIdentity?.frameworkType ?? null"
                                  [frameworkVersion]="candIdentity?.frameworkVersion ?? null"
                                  [frameworkName]="candIdentity?.frameworkName ?? null"
                                />
                              }
                              <span class="template-reuse-hint__name">{{ cand.name }}</span>
                            </div>
                            @if (cand.framework_ids.length) {
                              <span class="template-reuse-hint__framework-chip">
                                <mat-icon aria-hidden="true">layers</mat-icon>
                                {{
                                  templateReuseFrameworksKey(cand.framework_ids.length)
                                    | transloco: { count: cand.framework_ids.length }
                                }}
                              </span>
                            }
                          </div>
                          <a
                            class="template-reuse-hint__view"
                            [routerLink]="['/controls', cand.control_id]"
                            target="_blank"
                            rel="noopener"
                          >
                            <span>{{
                              'modals.create_control.template_reuse_view' | transloco
                            }}</span>
                            <mat-icon aria-hidden="true">open_in_new</mat-icon>
                          </a>
                        </li>
                      }
                    </ul>
                  </aside>
                }
              }
            } @else {
              <!-- Composite mode fixed vertical rhythm:
                     1. Multi-select picker (checkbox rows)
                     2. Selected-chips summary (only when ≥1 selected)
                     3. Composite reuse panel (only when ≥1 selected) -->
              <ng-container [ngTemplateOutlet]="pickerList" />

              @if (selectedControlTemplates().length > 0) {
                <div class="composite-selection">
                  <span class="composite-selection-label">
                    {{
                      compositeSelectedKey()
                        | transloco: { count: selectedControlTemplates().length }
                    }}
                  </span>
                  <div class="composite-chip-row">
                    @for (t of selectedControlTemplates(); track t.id) {
                      @let chipIdentity = templateFrameworkIdentity(t);
                      <span class="composite-chip">
                        @if (t.control_reference) {
                          <app-control-ref
                            [reference]="t.control_reference"
                            [frameworkType]="chipIdentity?.frameworkType ?? null"
                            [frameworkVersion]="chipIdentity?.frameworkVersion ?? null"
                            [frameworkName]="chipIdentity?.frameworkName ?? null"
                          />
                        }
                        <span class="composite-chip-name">{{
                          localizedField(t.name, t.name_da)
                        }}</span>
                        <button
                          type="button"
                          class="composite-chip-remove"
                          [attr.aria-label]="'modals.create_control.composite_remove' | transloco"
                          (click)="toggleCompositeTemplate(t)"
                        >
                          <mat-icon>close</mat-icon>
                        </button>
                      </span>
                    }
                  </div>
                </div>

                <!-- Composite reuse panel (VER-284). Two-way models
                     feed reuse_control_ids + create_gap_filler into
                     the ControlCreateRequest at submit. Candidates
                     default to [] until the fetch endpoint lands;
                     coverage summary + gap-filler toggle stay
                     meaningful with an empty candidate list. -->
                <app-composite-reuse-panel
                  [componentRefs]="compositeReusePanelRefs()"
                  [candidates]="reuseCandidates()"
                  [candidatesLoading]="reuseCandidatesLoading()"
                  [candidatesError]="reuseCandidatesError()"
                  [showActions]="false"
                  [(reuseControlIds)]="compositeReuseIds"
                  [(createGapFiller)]="compositeCreateGapFiller"
                  (retryRequested)="retryReuseFetch()"
                />
              }
            }
          </section>
        }

        <!-- Shared picker DOM (search + list + skeleton + empty + rows)
               used by both modes. The only per-mode difference is the row
               wrapper — a click-button in single mode vs a checkbox label
               in composite mode — so we branch on ``templatePickerMode()``
               inside the loop and reuse the row-body template ``rowBody``.
               Keeps the search bar, skeleton, empty state, and item
               styling in one place. -->
        <ng-template #pickerList>
          <div class="template-picker">
            <app-toolbar-search
              class="template-search"
              [placeholder]="'modals.create_control.template_search' | transloco"
              [ariaLabel]="'modals.create_control.template_search' | transloco"
              [maxWidth]="null"
              [value]="templateSearchQuery()"
              (valueChange)="onTemplateSearchValue($event)"
            />
            <div class="template-list">
              @if (controlTemplatesLoading()) {
                <app-picker-skeleton variant="compact" [count]="4" />
              } @else if (controlTemplatePickerRows().length === 0) {
                <p class="template-empty">
                  {{ 'modals.create_control.no_templates' | transloco }}
                </p>
              } @else {
                @for (row of controlTemplatePickerRows(); track row.template.id) {
                  @if (templatePickerMode() === 'single') {
                    <button
                      type="button"
                      class="template-item"
                      (click)="selectControlTemplate(row.template)"
                    >
                      <ng-container
                        [ngTemplateOutlet]="rowBody"
                        [ngTemplateOutletContext]="{ $implicit: row }"
                      />
                    </button>
                  } @else {
                    <label class="template-item template-item--multi">
                      <input
                        type="checkbox"
                        class="template-item-checkbox"
                        [checked]="selectedCompositeTemplateIds().has(row.template.id)"
                        (change)="toggleCompositeTemplate(row.template)"
                      />
                      <ng-container
                        [ngTemplateOutlet]="rowBody"
                        [ngTemplateOutletContext]="{ $implicit: row }"
                      />
                    </label>
                  }
                }
              }
            </div>
          </div>
        </ng-template>

        <ng-template #rowBody let-row>
          @if (row.template.control_reference) {
            @let rowIdentity = templateFrameworkIdentity(row.template);
            <app-control-ref
              [reference]="row.template.control_reference"
              [frameworkType]="rowIdentity?.frameworkType ?? null"
              [frameworkVersion]="rowIdentity?.frameworkVersion ?? null"
              [frameworkName]="rowIdentity?.frameworkName ?? null"
            />
          }
          <span class="template-item-text">
            <span class="template-name">{{ row.displayName }}</span>
            @if (row.rationalePreview) {
              <span class="template-rationale">{{ row.rationalePreview }}</span>
            }
          </span>
          @if (row.template.category) {
            <span class="template-category">{{ row.template.category }}</span>
          }
        </ng-template>
      </div>
    }

    <!-- ── STEP 3: DETAILS ──
         Name / category / description / assignees. Template + Composite
         picker moved to the dedicated Pick step above (VER-284) so this
         step focuses solely on the identifying + descriptive fields. -->
    @if (currentStep() === 'details') {
      <div class="step-body" [formGroup]="form">
        <div class="step-header-row">
          <p class="step-hint">{{ 'modals.create_control.basics_intro' | transloco }}</p>
          <span class="meta">{{
            'modals.create_control.step_header_created_by' | transloco: { name: currentUserName() }
          }}</span>
        </div>

        <div class="field">
          <label for="ctrl-name"
            >{{ 'modals.create_control.field_name' | transloco }} <span class="req">*</span></label
          >
          <input
            id="ctrl-name"
            type="text"
            class="input"
            formControlName="name"
            [placeholder]="'modals.create_control.name_placeholder' | transloco"
          />
        </div>

        <div class="field">
          <span class="field-label"
            >{{ 'modals.create_control.field_category' | transloco }}
            <span class="req">*</span></span
          >
          <mat-form-field appearance="outline" class="clean-select">
            <mat-select
              formControlName="category"
              [placeholder]="'modals.create_control.category_placeholder' | transloco"
            >
              @for (c of categoryOptions(); track c.value) {
                <mat-option [value]="c.value">{{ c.label }}</mat-option>
              }
            </mat-select>
          </mat-form-field>
          <span class="field-hint">{{ 'modals.create_control.category_helper' | transloco }}</span>
        </div>

        <div class="field">
          <label for="ctrl-desc">{{ 'modals.create_control.field_description' | transloco }}</label>
          <textarea
            id="ctrl-desc"
            class="input textarea"
            formControlName="description"
            [placeholder]="'modals.create_control.description_placeholder' | transloco"
            rows="4"
          ></textarea>
          @if (inheritedDescription(); as inherited) {
            <div class="inherited-description">
              <span class="field-hint">{{
                'modals.create_control.description_inherited_hint' | transloco
              }}</span>
              <p class="inherited-description-text">{{ inherited }}</p>
            </div>
          }
        </div>

        <div class="field">
          <span class="field-label">{{ 'modals.create_control.field_assignees' | transloco }}</span>
          <app-assignee-picker
            [assigneeIds]="selectedAssignees()"
            [members]="orgMembers()"
            [readonly]="false"
            (assigneesChange)="selectedAssignees.set($event)"
          />
        </div>
      </div>
    }

    <!-- ── STEP 3: CONFIGURATION ── -->
    @if (currentStep() === 'config') {
      <div class="step-body" [formGroup]="form">
        @if (error()) {
          <div
            role="alert"
            [class.denied-banner]="errorIsDenial()"
            [class.alert]="!errorIsDenial()"
          >
            {{ error() }}
          </div>
        }

        <p class="step-hint">{{ 'modals.create_control.linking_intro' | transloco }}</p>

        <!-- Linking -->
        <p class="section-label">{{ 'modals.create_control.linking_heading' | transloco }}</p>
        <div class="row">
          <div class="field">
            <span class="field-label">{{
              'modals.create_control.field_framework' | transloco
            }}</span>
            <mat-form-field appearance="outline" class="clean-select">
              @if (loadingFrameworks()) {
                <mat-select
                  disabled
                  [placeholder]="'modals.create_control.framework_loading' | transloco"
                >
                  <mat-option>{{
                    'modals.create_control.framework_loading' | transloco
                  }}</mat-option>
                </mat-select>
              } @else if (frameworkOptions().length === 0) {
                <mat-select
                  disabled
                  [placeholder]="'modals.create_control.framework_none_available' | transloco"
                >
                </mat-select>
              } @else {
                <mat-select
                  formControlName="framework_ids"
                  multiple
                  [placeholder]="'modals.create_control.framework_select' | transloco"
                  panelClass="entity-select-panel"
                >
                  <!-- Trigger override — without this Material builds
                       the trigger from the mat-option's textContent,
                       which would include the SVG <text> elements
                       inside <app-framework-icon> (family + number
                       glyphs). Anchor the trigger to the joined names
                       instead. -->
                  <mat-select-trigger>{{
                    selectedFrameworkNames().join(', ') ||
                      ('modals.create_control.framework_select' | transloco)
                  }}</mat-select-trigger>
                  @for (f of frameworkOptions(); track f.id) {
                    <mat-option [value]="f.id" [title]="f.name">
                      <span class="entity-opt">
                        <app-framework-icon [type]="f.framework_type" size="inline" />
                        <span class="entity-opt-name">{{ f.name }}</span>
                      </span>
                    </mat-option>
                  }
                </mat-select>
              }
            </mat-form-field>
          </div>
          <div class="field">
            <span class="field-label">{{
              'modals.create_control.field_document' | transloco
            }}</span>
            <mat-form-field appearance="outline" class="clean-select">
              @if (loadingDocuments()) {
                <mat-select
                  disabled
                  [placeholder]="'modals.create_control.document_loading' | transloco"
                >
                  <mat-option>{{
                    'modals.create_control.document_loading' | transloco
                  }}</mat-option>
                </mat-select>
              } @else if (documentOptions().length === 0) {
                <mat-select
                  disabled
                  [placeholder]="'modals.create_control.document_none_available' | transloco"
                >
                </mat-select>
              } @else {
                <mat-select
                  formControlName="document_ids"
                  multiple
                  [placeholder]="'modals.create_control.document_select' | transloco"
                  panelClass="entity-select-panel"
                >
                  <mat-select-trigger>{{
                    selectedDocumentNames() || ('modals.create_control.document_select' | transloco)
                  }}</mat-select-trigger>
                  @for (doc of documentOptions(); track doc.id) {
                    <mat-option [value]="doc.id" [title]="doc.name">
                      <span class="entity-opt">
                        <app-kind-icon [kind]="doc.kind" size="xs" />
                        <span class="entity-opt-body">
                          <span class="entity-opt-name">{{ doc.name }}</span>
                          <span class="entity-opt-meta">
                            <span class="entity-opt-type">{{ doc.typeLabel }}</span>
                            <span class="entity-opt-status" [attr.data-status]="doc.status">{{
                              doc.status | lowercase
                            }}</span>
                          </span>
                        </span>
                      </span>
                    </mat-option>
                  }
                </mat-select>
              }
            </mat-form-field>
          </div>
        </div>

        <!-- Schedule -->
        <div class="divider"></div>
        <p class="section-label">{{ 'modals.create_control.schedule_heading' | transloco }}</p>
        <div class="field">
          <span class="field-label">{{
            'modals.create_control.field_recurrence' | transloco
          }}</span>
          <mat-form-field appearance="outline" class="clean-select">
            <mat-select
              formControlName="recurrence"
              [placeholder]="'modals.create_control.recurrence_placeholder' | transloco"
            >
              <mat-option [value]="null">{{
                'modals.create_control.recurrence_option_none' | transloco
              }}</mat-option>
              @for (r of recurrenceOptions(); track r.value) {
                <mat-option [value]="r.value">{{ r.label }}</mat-option>
              }
            </mat-select>
          </mat-form-field>
        </div>

        @if (isCustomRecurrence()) {
          <div class="field">
            <span class="field-label">{{
              'modals.create_control.interval_days_label' | transloco
            }}</span>
            <input
              type="number"
              min="1"
              step="1"
              inputmode="numeric"
              class="input"
              formControlName="custom_recurrence_days"
              [placeholder]="'modals.create_control.interval_placeholder' | transloco"
              (keydown)="onIntervalKeydown($event)"
              (paste)="onIntervalPaste($event)"
            />
            @if (customRecurrenceHint(); as hint) {
              <span class="field-hint">{{ hint }}</span>
            }
          </div>
        }

        @if (showDueDateBlock()) {
          <div class="inset-row">
            <div class="field">
              <span class="field-label">{{
                'modals.create_control.field_start_date' | transloco
              }}</span>
              <input type="date" class="input" formControlName="first_due_date" />
            </div>
          </div>
        }
      </div>
    }

    <!-- ── STEP 4: TASKS ── -->
    @if (currentStep() === 'tasks') {
      <div class="step-body">
        <p class="step-hint">{{ 'modals.create_control.tasks_intro' | transloco }}</p>

        @if (!taskFormOpen() && taskTemplates().length === 0) {
          <app-empty-state
            variant="panel"
            icon="assignment"
            [title]="'modals.create_control.no_tasks_title' | transloco"
            [message]="'modals.create_control.no_tasks_message' | transloco"
          />
        } @else if (!taskFormOpen()) {
          <div class="task-list">
            @for (task of taskTemplates(); track $index) {
              <div class="task-card">
                <span class="task-icon" [attr.data-type]="task.task_type">
                  <mat-icon>{{ taskTypeIcon(task.task_type) }}</mat-icon>
                </span>
                <div class="task-info">
                  <p class="task-title">{{ task.title }}</p>
                  <p class="task-meta">
                    @if (task.recurring) {
                      <span
                        >{{ 'modals.create_control.task_due_prefix' | transloco }}
                        {{ recurringTaskDueDate() ?? '—' }}</span
                      >
                    } @else {
                      <span
                        >{{ 'modals.create_control.task_due_prefix' | transloco }}
                        {{ formatTaskDate(task.due_date) }}</span
                      >
                    }
                    <span class="task-meta-sep">·</span>
                    <span
                      >{{ 'modals.create_control.task_type_prefix' | transloco }}
                      {{ taskTypeLabel(task.task_type) }}</span
                    >
                  </p>
                </div>
                <div class="task-actions">
                  <button
                    type="button"
                    class="task-action-btn"
                    (click)="openEditTask($index)"
                    [attr.aria-label]="'modals.create_control.edit_task_aria' | transloco"
                  >
                    <mat-icon>edit</mat-icon>
                  </button>
                  <button
                    type="button"
                    class="task-action-btn"
                    (click)="deleteTask($index)"
                    [attr.aria-label]="'modals.create_control.delete_task_aria' | transloco"
                  >
                    <mat-icon>close</mat-icon>
                  </button>
                </div>
              </div>
            }
          </div>
        }

        @if (taskFormOpen()) {
          <div class="task-form-card" [formGroup]="taskForm">
            <div class="field">
              <label for="task-title">{{ 'modals.create_control.field_title' | transloco }}</label>
              <input
                id="task-title"
                type="text"
                class="input"
                formControlName="title"
                [placeholder]="'modals.create_control.title_placeholder' | transloco"
              />
            </div>

            <div class="field">
              <span class="field-label">{{
                'modals.create_control.field_task_type' | transloco
              }}</span>
              <div class="pill-row">
                @for (type of taskTypeOptions(); track type.value) {
                  <button
                    type="button"
                    class="type-pill"
                    [class.active]="taskForm.controls.task_type.value === type.value"
                    (click)="selectTaskType(type.value)"
                  >
                    {{ type.label }}
                  </button>
                }
              </div>
              @if (taskTypeDescription(); as desc) {
                <p class="task-type-desc">{{ desc }}</p>
              }
              @if (requiresLink()) {
                <div class="link-required-notice">
                  <mat-icon>warning</mat-icon>
                  <span>{{
                    'modals.create_control.requires_link_notice'
                      | transloco: { type: taskTypeLabel(taskForm.controls.task_type.value) }
                  }}</span>
                </div>
              }
            </div>

            <div class="field">
              <span class="field-label">{{ 'modals.create_control.link_label' | transloco }}</span>
              @if (!taskForm.controls.task_type.value) {
                <div class="link-placeholder">
                  {{ 'modals.create_control.pick_task_type_helper' | transloco }}
                </div>
              } @else if (isUploadTask()) {
                <div class="link-locked">
                  <span class="link-locked-label">{{
                    'modals.create_control.linked_to' | transloco
                  }}</span>
                  <span class="link-locked-value">{{
                    'modals.create_control.this_control' | transloco
                  }}</span>
                </div>
              } @else if (showEntityPicker()) {
                <div class="link-card-row">
                  @for (opt of taskLinkOptions(); track opt.value) {
                    <button
                      type="button"
                      class="link-mini-card"
                      [class.active]="taskForm.controls.link_kind.value === opt.value"
                      (click)="selectTaskLink(opt.value)"
                    >
                      <span class="link-mini-icon" aria-hidden="true">
                        <app-section-icon [name]="opt.sectionIcon" />
                      </span>
                      <span class="link-mini-label">{{ opt.label }}</span>
                    </button>
                  }
                </div>
              }
            </div>

            <!-- Entity picker lives outside .field so the global
                 `.field input:not(...)` rule from styles/forms.css doesn't
                 override the borderless search input. -->
            @if (showEntityPicker() && taskForm.controls.link_kind.value; as kind) {
              @if (isCreateTask()) {
                @if (showSubtypePicker()) {
                  <div class="entity-list">
                    @for (opt of createEntitySubtypeOptions(); track opt.value) {
                      <button
                        type="button"
                        class="entity-item"
                        [class.selected]="
                          taskForm.controls.create_entity_subtype.value === opt.value
                        "
                        [attr.data-kind]="kind"
                        (click)="selectEntitySubtype(opt.value)"
                      >
                        <!-- Subtype icon — reuses <app-entity-icon>
                             with a per-row iconInput built from the
                             subtype code (document) or framework type
                             (framework). Same primitive, same vocab. -->
                        @if (kind === 'document') {
                          <app-entity-icon
                            [entity]="{
                              kind: 'document',
                              docKind: getDocKindFromSubtypeCode(opt.value),
                            }"
                          />
                        } @else if (kind === 'framework') {
                          <app-entity-icon
                            [entity]="{ kind: 'framework', frameworkType: $any(opt.value) }"
                          />
                        }
                        <span class="entity-info">
                          <span class="entity-name">{{ opt.label }}</span>
                          <span class="entity-subtitle">{{ opt.description }}</span>
                        </span>
                        <span
                          class="entity-radio"
                          [class.checked]="
                            taskForm.controls.create_entity_subtype.value === opt.value
                          "
                        ></span>
                      </button>
                    }
                  </div>
                } @else if (kind === 'assessment') {
                  <div class="link-locked">
                    <span class="link-locked-label">{{
                      'modals.create_control.type_prefix' | transloco
                    }}</span>
                    <span class="link-locked-value">{{
                      'modals.create_control.ai_risk_assessment' | transloco
                    }}</span>
                  </div>
                }
                <!-- control/vendor: no further selection needed -->
              } @else {
                <div class="entity-picker">
                  <app-toolbar-search
                    class="entity-search"
                    [placeholder]="
                      'modals.create_control.entity_search_placeholder' | transloco: { kind: kind }
                    "
                    [ariaLabel]="
                      'modals.create_control.entity_search_aria' | transloco: { kind: kind }
                    "
                    [maxWidth]="null"
                    [value]="entitySearchQuery()"
                    (valueChange)="onEntitySearchValue($event)"
                  />
                  <div class="entity-list">
                    @if (isEntityLoading()) {
                      <div class="entity-loading">
                        <mat-icon [appSpin]="true" aria-hidden="true">sync</mat-icon>
                        <span>{{ 'modals.create_control.entity_loading' | transloco }}</span>
                      </div>
                    } @else if (filteredEntityItems().length === 0) {
                      <p class="entity-empty">
                        {{ 'modals.create_control.entity_no_results' | transloco }}
                      </p>
                    } @else {
                      @for (item of filteredEntityItems(); track item.id) {
                        <button
                          type="button"
                          class="entity-item"
                          [class.selected]="taskForm.controls.link_entity_id.value === item.id"
                          [attr.data-kind]="kind"
                          (click)="selectTaskEntity(item.id)"
                        >
                          <!-- Canonical entity icon primitive — dispatches
                               to the right icon component per kind
                               (document→kind, control→type-icon per row-
                               state, framework→cert-seal, vendor→section,
                               assessment→type-icon per type). Same
                               vocabulary every other surface uses. -->
                          <app-entity-icon [entity]="item.iconInput" />
                          <span class="entity-info">
                            <span class="entity-name">{{ item.name }}</span>
                            @if (item.subtitle) {
                              <span class="entity-subtitle">{{ item.subtitle }}</span>
                            }
                          </span>
                          <span
                            class="entity-radio"
                            [class.checked]="taskForm.controls.link_entity_id.value === item.id"
                          ></span>
                        </button>
                      }
                    }
                  </div>
                </div>
              }
            }

            <div class="field">
              <label for="task-desc-template"
                >{{ 'modals.create_control.task_description_label' | transloco }}
                <span class="optional">{{
                  'modals.create_control.task_description_optional' | transloco
                }}</span></label
              >
              <textarea
                id="task-desc-template"
                class="input textarea"
                formControlName="description"
                [placeholder]="'modals.create_control.task_description_placeholder' | transloco"
                rows="3"
              ></textarea>
            </div>

            <div class="toggle-row">
              <span class="toggle-label">{{
                'modals.create_control.requires_approval' | transloco
              }}</span>
              <mat-slide-toggle formControlName="needs_approval" [disabled]="isCreateTask()" />
            </div>
            @if (isCreateTask()) {
              <p class="toggle-hint">
                {{ 'modals.create_control.approval_required_helper' | transloco }}
              </p>
            }
            <div class="toggle-row">
              <span class="toggle-label">{{
                'modals.create_control.recurring_task' | transloco
              }}</span>
              <mat-slide-toggle
                formControlName="recurring"
                [disabled]="!isControlRecurrent() || isCreateTask()"
              />
            </div>
            @if (isCreateTask()) {
              <p class="toggle-hint">
                {{ 'modals.create_control.create_tasks_no_recur_helper' | transloco }}
              </p>
            } @else if (!isControlRecurrent()) {
              <p class="toggle-hint">
                {{ 'modals.create_control.no_recurrence_helper' | transloco }}
              </p>
            } @else if (taskRecurringValue()) {
              <p class="toggle-hint">
                {{ 'modals.create_control.follows_cycle_helper' | transloco }}
              </p>
            }
            @if (showTaskDueDateField()) {
              <div class="field">
                <label for="modal-task-due"
                  >{{ 'modals.create_control.field_due_date' | transloco }}
                  <span class="optional">{{
                    'modals.create_control.due_date_optional' | transloco
                  }}</span></label
                >
                <input
                  id="modal-task-due"
                  type="date"
                  class="input"
                  formControlName="due_date"
                  [min]="todayDate()"
                />
              </div>
            }

            <div class="task-form-footer">
              <app-button type="secondary" size="small" (clicked)="cancelTaskForm()">
                {{ 'modals.create_control.secondary_cancel' | transloco }}
              </app-button>
              <app-button
                type="primary"
                size="small"
                [disabled]="!taskFormSaveEnabled()"
                (clicked)="saveTask()"
              >
                {{
                  editingTaskIndex() !== null
                    ? ('modals.create_control.save_changes' | transloco)
                    : ('modals.create_control.add_task' | transloco)
                }}
              </app-button>
            </div>
          </div>
        } @else {
          <button type="button" class="add-task-btn" (click)="openAddTask()">
            <mat-icon>add</mat-icon>
            <span>{{ 'modals.create_control.add_task' | transloco }}</span>
          </button>
        }
      </div>
    }

    <!-- ── STEP 5: REVIEW ── -->
    @if (currentStep() === 'review') {
      <div class="step-body">
        @if (error()) {
          <div
            role="alert"
            [class.denied-banner]="errorIsDenial()"
            [class.alert]="!errorIsDenial()"
          >
            {{ error() }}
          </div>
        }

        <p class="step-hint">{{ 'modals.create_control.review_intro' | transloco }}</p>

        <!-- Details -->
        <div class="review-section">
          <p class="section-label">
            {{ 'modals.create_control.review_details_heading' | transloco }}
          </p>
          <div class="review-grid">
            <div class="review-item">
              <span class="review-label">{{
                'modals.create_control.review_name' | transloco
              }}</span>
              <span class="review-value">{{ form.controls.name.value }}</span>
            </div>
            <div class="review-item">
              <span class="review-label">{{
                'modals.create_control.field_language' | transloco
              }}</span>
              <span class="review-value">
                <app-language-badge [language]="templateLanguage()" appearance="pill" />
              </span>
            </div>
            @if (reviewCategoryLabel(); as catLabel) {
              <div class="review-item">
                <span class="review-label">{{
                  'modals.create_control.review_category' | transloco
                }}</span>
                <span class="review-value">{{ catLabel }}</span>
              </div>
            }
            @let reviewDescriptionText = reviewDescription();
            @if (reviewDescriptionText) {
              <div class="review-item review-item-full">
                <span class="review-label">{{
                  'modals.create_control.review_description' | transloco
                }}</span>
                <span class="review-value review-value-desc">{{ reviewDescriptionText }}</span>
              </div>
            }
            @if (selectedOrg(); as org) {
              <div class="review-item">
                <span class="review-label">{{
                  'modals.create_control.review_organization' | transloco
                }}</span>
                <span class="review-value">{{ org.name }}</span>
              </div>
            }
            <div class="review-item">
              <span class="review-label">{{
                'modals.create_control.review_created_by' | transloco
              }}</span>
              <span class="review-value">{{ currentUserName() }}</span>
            </div>
            @if (selectedAssignees().length > 0) {
              <div class="review-item">
                <span class="review-label">{{
                  'modals.create_control.field_assignees' | transloco
                }}</span>
                <span class="review-value">
                  <app-assignee-picker
                    [assigneeIds]="selectedAssignees()"
                    [members]="orgMembers()"
                    [readonly]="true"
                    [compact]="true"
                  />
                </span>
              </div>
            }
            @if (reviewTemplateRef(); as ref) {
              <div class="review-item">
                <span class="review-label">{{
                  'modals.create_control.review_template' | transloco
                }}</span>
                <span class="review-value">{{ ref }}</span>
              </div>
            }
          </div>
        </div>

        <!-- Linking -->
        @if (selectedFrameworkNames().length > 0 || selectedDocumentsList().length > 0) {
          <div class="divider"></div>
          <div class="review-section">
            <p class="section-label">
              {{ 'modals.create_control.review_linked_resources' | transloco }}
            </p>
            <div class="review-grid">
              @if (selectedFrameworkNames().length > 0) {
                <div class="review-item">
                  <span class="review-label">{{
                    'modals.create_control.review_frameworks' | transloco
                  }}</span>
                  <span class="review-value">{{ selectedFrameworkNames().join(', ') }}</span>
                </div>
              }
              @if (selectedDocumentsList().length > 0) {
                <div class="review-item">
                  <span class="review-label">{{
                    'modals.create_control.review_documents' | transloco
                  }}</span>
                  <span class="review-value">{{ selectedDocumentNames() }}</span>
                </div>
              }
            </div>
          </div>
        }

        <!-- Tasks -->
        @if (taskTemplates().length > 0) {
          <div class="divider"></div>
          <div class="review-section">
            <p class="section-label">
              {{
                'modals.create_control.review_tasks_heading'
                  | transloco: { count: taskTemplates().length }
              }}
            </p>
            <div class="task-list">
              @for (task of taskTemplates(); track $index) {
                <div class="task-card task-card-readonly">
                  <span class="task-icon" [attr.data-type]="task.task_type">
                    <mat-icon>{{ taskTypeIcon(task.task_type) }}</mat-icon>
                  </span>
                  <div class="task-info">
                    <p class="task-title">{{ task.title }}</p>
                    <p class="task-meta">
                      @if (task.recurring) {
                        <span
                          >{{ 'modals.create_control.task_due_prefix' | transloco }}
                          {{ recurringTaskDueDate() ?? '—' }}</span
                        >
                      } @else {
                        <span
                          >{{ 'modals.create_control.task_due_prefix' | transloco }}
                          {{ formatTaskDate(task.due_date) }}</span
                        >
                      }
                      <span class="task-meta-sep">·</span>
                      <span
                        >{{ 'modals.create_control.task_type_prefix' | transloco }}
                        {{ taskTypeLabel(task.task_type) }}</span
                      >
                    </p>
                  </div>
                </div>
              }
            </div>
          </div>
        }

        <!-- Schedule -->
        @if (reviewRecurrenceLabel()) {
          <div class="divider"></div>
          <div class="review-section">
            <p class="section-label">
              {{ 'modals.create_control.review_schedule' | transloco }}
            </p>
            <div class="review-grid">
              <div class="review-item">
                <span class="review-label">{{
                  'modals.create_control.review_recurrence' | transloco
                }}</span>
                <span class="review-value">{{ reviewRecurrenceLabel() }}</span>
              </div>
              @if (isCustomRecurrence() && form.controls.custom_recurrence_days.value) {
                <div class="review-item">
                  <span class="review-label">{{
                    'modals.create_control.review_interval' | transloco
                  }}</span>
                  <span class="review-value">{{
                    'modals.create_control.interval_days_unit'
                      | transloco: { days: form.controls.custom_recurrence_days.value }
                  }}</span>
                </div>
              }
              @if (form.controls.first_due_date.value) {
                <div class="review-item">
                  <span class="review-label">{{
                    'modals.create_control.review_start_date' | transloco
                  }}</span>
                  <span class="review-value">{{
                    formatTaskDate(form.controls.first_due_date.value)
                  }}</span>
                </div>
              }
            </div>
          </div>
        }

        <!-- Backfill notice -->
        @if (backfillTaskCount() > 0) {
          <div class="divider"></div>
          <div class="backfill-notice">
            <mat-icon aria-hidden="true">info</mat-icon>
            <span>{{
              (backfillTaskCount() === 1
                ? 'modals.create_control.backfill_notice_one'
                : 'modals.create_control.backfill_notice_other'
              ) | transloco: { count: backfillTaskCount() }
            }}</span>
          </div>
        }
      </div>
    }
  }
</app-modal>
